// Revise ▸ Comments (the Mac's CommentsPage and CommentSourcesColumn): every open comment and suggestion in the
// manuscript, in reading order — reviewers', the writer's own and the checks' — filtered by who they're from. Show opens
// the passage in Write, following it to where it is now; Resolve, Accept and Reject work here too. While the contents are
// filtered, "Only filtered scenes" (on by default, kept in this browser) keeps to the scenes the filter lets through.
import { useMemo, useState } from 'react';
import { orderedFragmentIDs, type OpenedProject } from '@writing-suite/core';
import type { PageProps } from '../shell/ProjectShell.tsx';
import { announce, usePreference } from '../shell/context.ts';
import { useWorkspace } from '../shell/workspace.ts';
import { categoryOf } from '@writing-suite/core/revise';
import { commentCategoriesOf } from '../store/revise.ts';
import { fragmentEntries, resolveEntry, type MarginEntry } from './comments.ts';
import { CategoryDot, CommentCategoriesDialog } from './CommentCategories.tsx';
import { MarginCard } from './Margin.tsx';

/** The category shown: all (null), one category's comments, or the comments in none. */
type CategoryFilter = null | { id: string } | 'none';

export function CommentsPage({ project, live }: PageProps) {
  const ws = useWorkspace();
  const [from, setFrom] = useState<string | null>(null);
  const [inCategory, setInCategory] = useState<CategoryFilter>(null);
  const [editingCategories, setEditingCategories] = useState(false);
  const [followsFilter, setFollowsFilter] = usePreference('commentsFollowFilter', true);
  const filter = ws?.filter?.view ? ws.filter : null;
  const categories = commentCategoriesOf(project);
  const canWrite = !!ws?.canWrite && !!live?.writable;
  const items = useMemo(() => {
    const opened = project as OpenedProject;
    const manuscript = opened.record?.arrangements.find((a) => a.role === 'manuscript');
    if (!manuscript) return [];
    const seen = new Set<string>();
    return orderedFragmentIDs(manuscript)
      .filter((id) => !seen.has(id) && (seen.add(id), true))
      .flatMap((id) => fragmentEntries(opened, id, null, { resolve: false, synopsis: true }));
  }, [project]);
  const counts = new Map<string, number>();
  for (const i of items) counts.set(i.author, (counts.get(i.author) ?? 0) + 1);
  const authors = [...counts.keys()].sort((a, b) => a.localeCompare(b));
  // Suggestions have no category: they're under neither a category nor "Uncategorised".
  const comments = items.filter((i) => i.kind !== 'suggestion');
  const categoryCounts = new Map<string, number>();
  for (const c of comments) {
    const id = categoryOf(c, categories)?.id;
    if (id) categoryCounts.set(id, (categoryCounts.get(id) ?? 0) + 1);
  }
  const uncategorised = comments.filter((c) => !categoryOf(c, categories)).length;
  // A category deleted while it was chosen shows everything again.
  const chosen = inCategory !== null && inCategory !== 'none' && !categories.some((c) => c.id === inCategory.id) ? null : inCategory;
  const inChosen = (i: MarginEntry) =>
    chosen === null || (i.kind !== 'suggestion' && (chosen === 'none' ? !categoryOf(i, categories) : categoryOf(i, categories)?.id === chosen.id));
  const shown = items.filter((i) => (from === null || i.author === from) && inChosen(i) && (!followsFilter || !filter || filter.matchesFragment(i.fragmentId)));

  const show = (e: MarginEntry) => {
    const doc = live?.doc(`fragment.${e.fragmentId}`) ?? (project as OpenedProject).fragmentDoc?.(e.fragmentId) ?? null;
    const range = doc ? resolveEntry(doc, e) : null;
    if (!range && e.kind !== 'synopsis') announce('That passage isn’t in the text any more; showing its scene.');
    ws?.reveal(range ? { fragmentId: e.fragmentId, from: range[0], to: range[1] } : { fragmentId: e.fragmentId });
  };

  return (
    <div className="comments-page">
      <header className="page-head">
        <h1 tabIndex={-1}>Comments</h1>
        <p className="page-sub">Every open comment and suggestion in the manuscript, in reading order.</p>
        {filter ? (
          <label className="comments-follow-filter">
            <input type="checkbox" role="switch" checked={followsFilter} onChange={(e) => setFollowsFilter(e.target.checked)} />
            Only filtered scenes
          </label>
        ) : null}
      </header>
      <div className="comments-layout">
        <nav className="comment-sources" aria-label="From">
          <h2 className="eyebrow">From</h2>
          <ul>
            <li>
              <button type="button" className="source-row" aria-pressed={from === null} onClick={() => setFrom(null)}>
                <span>Everyone</span>
                <span className="source-count">{items.length}</span>
              </button>
            </li>
            {authors.map((a) => (
              <li key={a}>
                <button type="button" className="source-row" aria-pressed={from === a} onClick={() => setFrom(a)}>
                  <span>{a || 'Someone'}</span>
                  <span className="source-count">{counts.get(a)}</span>
                </button>
              </li>
            ))}
          </ul>
          {categories.length ? (
            <>
              <h2 className="eyebrow">Category</h2>
              <ul aria-label="Category">
                <li>
                  <button type="button" className="source-row" aria-pressed={chosen === null} onClick={() => setInCategory(null)}>
                    <span>Any</span>
                  </button>
                </li>
                {categories.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      className="source-row"
                      data-category={c.id}
                      aria-pressed={chosen !== null && chosen !== 'none' && chosen.id === c.id}
                      onClick={() => setInCategory({ id: c.id })}
                    >
                      <span>
                        <CategoryDot category={c} />
                        {c.name}
                      </span>
                      <span className="source-count">{categoryCounts.get(c.id) ?? 0}</span>
                    </button>
                  </li>
                ))}
                <li>
                  <button type="button" className="source-row" aria-pressed={chosen === 'none'} onClick={() => setInCategory('none')}>
                    <span>Uncategorised</span>
                    <span className="source-count">{uncategorised}</span>
                  </button>
                </li>
              </ul>
            </>
          ) : null}
          {canWrite ? (
            <button type="button" className="link-button edit-categories" onClick={() => setEditingCategories(true)}>
              {categories.length ? 'Edit categories…' : 'Comment categories…'}
            </button>
          ) : null}
        </nav>
        <section className="comment-list" aria-label={from === null ? 'Comments from everyone' : `Comments from ${from}`} data-count={shown.length}>
          {shown.length === 0 ? <p className="empty-note">{items.length === 0 ? 'No open comments or suggestions in the manuscript.' : chosen === null ? 'None from them.' : 'None in this category.'}</p> : null}
          {shown.map((e) => (
            <MarginCard
              key={e.id}
              entry={e}
              active={false}
              showScene
              actions={{ live, canWrite, canComment: !!ws?.canComment }}
              onChoose={() => show(e)}
              chooseLabel="Show in Write"
              categories={categories}
              onEditCategories={canWrite ? () => setEditingCategories(true) : undefined}
            />
          ))}
        </section>
      </div>
      {editingCategories && live ? <CommentCategoriesDialog project={project} live={live} onClose={() => setEditingCategories(false)} /> : null}
    </div>
  );
}
